<template>
    <div class="quiz-container">
        <headerPage />

        <!-- 试卷标题 -->
        <div class="quiz-header">
            <h2>{{ examTitle }}</h2>
            <p>共 {{ questionList.length }} 题</p>
        </div>

        <div class="quiz-content">
            <!-- 左侧试题区域 -->
            <div class="question-area">
                <el-card v-for="(question, index) in questionList" :key="index" class="question-card">
                    <div class="question-title">
                        <span>
                            {{ index + 1 }}. {{ question.text }}
                            <span v-if="question.type === 'multiple'">(多选)</span>
                        </span>
                    </div>

                    <!-- 选项区域 -->
                    <div class="question-options">
                        <!-- 单选题、判断题 -->
                        <el-radio-group v-if="question.type === 'single' || question.type === 'truefalse'"
                            v-model="question.userAnswer" @change="onAnswerChange(question)">
                            <el-radio v-for="option in question.options" :key="option.value" :label="option.value"
                                class="option-item" :class="getOptionClass(question, option.value)">
                                {{ option.value }}. {{ option.label }}
                            </el-radio>
                        </el-radio-group>

                        <!-- 多选题 -->
                        <el-checkbox-group v-else-if="question.type === 'multiple'" v-model="question.userAnswer"
                            @change="onAnswerChange(question)">
                            <el-checkbox v-for="option in question.options" :key="option.value" :label="option.value"
                                class="option-item" :class="getOptionClass(question, option.value)">
                                {{ option.value }}. {{ option.label }}
                            </el-checkbox>
                        </el-checkbox-group>
                    </div>

                    <!-- 解析区域 -->
                    <div class="question-analysis">
                        <el-button type="text" @click="toggleExplanation(index)">
                            {{ question.showExplanation ? "收起解析" : "查看解析" }}
                        </el-button>
                        <el-collapse-transition>
                            <div v-show="question.showExplanation" class="explanation">
                                <p>解析：</p>
                                <p :class="question.isCorrect ? 'feedback-correct' : 'feedback-wrong'">
                                    {{ question.isCorrect ? '回答正确' : '回答错误' }}
                                </p>
                                <p>正确答案：{{ question.correct }}</p>
                                <p>{{ question.explanation }}</p>
                            </div>
                        </el-collapse-transition>
                    </div>
                </el-card>
            </div>

            <!-- 右侧答题卡 -->
            <div class="question-card-panel">
                <!-- 倒计时显示 -->
                <div v-if="timeLeft > 0" class="countdown">
                    剩余时间：<span class="time-text">{{ formattedTime }}</span>
                </div>

                <!-- 顶部标题和折叠按钮 -->
                <div class="card-header">
                    <span class="card-title">答题卡</span>
                    <el-button type="text" size="mini" @click="toggleCardCollapse">
                        {{ isCardCollapsed ? "展开答题卡" : "收起答题卡" }}
                    </el-button>
                </div>

                <!-- 中间题号区域 -->
                <transition name="el-fade-in">
                    <div v-show="!isCardCollapsed" class="question-nav">
                        <el-button v-for="(q, idx) in questionList" :key="idx" @click="scrollToQuestion(idx)"
                            :class="{ answered: hasAnswered(q) }" type="default" size="mini">
                            {{ idx + 1 }}
                        </el-button>
                    </div>
                </transition>

                <!-- 底部提交按钮 -->
                <div style="margin-top: 15px; text-align: center;">
                    <el-button type="primary" @click="submitAnswers">立即提交</el-button>
                </div>
            </div>
        </div>

        <bottomPage />
    </div>
</template>

<script>
import headerPage from "@/components/header/header";
import bottomPage from "@/components/bottom/bottom";
import * as PaperApi from "@/api/paper";

export default {
    components: {
        headerPage,
        bottomPage,
    },
    data() {
        return {
            paperId: 0,
            examTitle: "",
            timeLeft: 0,
            questionList: [],
            isCardCollapsed: false,
            loadingInstance: null,
            timer: null,
        };
    },
    computed: {
        formattedTime() {
            const minutes = Math.floor(this.timeLeft / 60);
            const seconds = this.timeLeft % 60;
            return `${minutes}:${seconds < 10 ? "0" : ""}${seconds}`;
        },
    },
    created() {
        this.loadingInstance = this.$loading({
            lock: true,
            text: "正在加载试题，请稍候...",
            spinner: "el-icon-loading",
            background: "rgba(0, 0, 0, 0.4)",
        });
        this.tryRestoreOrLoadPaper();
    },
    methods: {
        toggleCardCollapse() {
            this.isCardCollapsed = !this.isCardCollapsed;
        },
        toggleExplanation(index) {
            this.questionList[index].showExplanation =
                !this.questionList[index].showExplanation;
        },
        scrollToQuestion(index) {
            const element = document.querySelectorAll(".question-card")[index];
            if (element) {
                element.scrollIntoView({ behavior: "smooth", block: "start" });
            }

            this.saveProgress();
        },
        hasAnswered(question) {
            return Array.isArray(question.userAnswer)
                ? question.userAnswer.length > 0
                : question.userAnswer !== "";
        },
        // 主入口：尝试从缓存还原或请求服务器
        async tryRestoreOrLoadPaper() {
            const userId = this.$store.getters.userId;
            const cacheKey = `paper_cache_${this.$route.params.type}_${this.$route.params.courseId}_${this.$route.params.chapterId}_${userId}`;
            const cachedStr = localStorage.getItem(cacheKey);
            let cached = null;

            if (cachedStr) {
                cached = JSON.parse(cachedStr);
                const now = Date.now();
                if (now - cached.saveTime > 3600 * 1000) {
                    // 缓存超时
                    localStorage.removeItem(cacheKey);
                    cached = null;
                }
            }

            if (cached) {
                // ⬅️ 完全从缓存还原
                this.paperId = cached.paperId;
                this.examTitle = cached.examTitle;
                this.timeLeft = cached.timeLeft;
                this.isCardCollapsed = cached.isCardCollapsed;
                this.questionList = cached.questionList;
                if (this.timeLeft > 0) {
                    this.startCountdown();
                }
            } else {
                // 没缓存才请求接口
                if (+this.$route.params.type === 0) {
                    await this.loadPaper();
                } else if (+this.$route.params.type === 1) {
                    await this.loadWrongPaper();
                }
                if (this.timeLeft > 0) {
                    this.startCountdown();
                }
            }

            if (this.loadingInstance) {
                this.loadingInstance.close();
            }

            // ✅ 有题目时才缓存
            if (this.questionList.length > 0) {
                this.saveProgress();
            }
        },
        restoreAnswers(answerData) {
            if (!Array.isArray(answerData)) return;
            answerData.forEach(saved => {
                const q = this.questionList.find(item => item.id === saved.id);
                if (q) {
                    // 注意：深拷贝 array，避免响应式污染
                    q.userAnswer = Array.isArray(saved.userAnswer)
                        ? [...saved.userAnswer]
                        : saved.userAnswer;
                    q.showExplanation = saved.showExplanation;
                    q.isCorrect = saved.isCorrect;
                }
            });
        },
        async loadPaper() {
            try {
                const res = await PaperApi.getChapterPaper(
                    this.$route.params.courseId,
                    this.$route.params.chapterId
                );
                if (res.code !== 0 || !res.data) return;

                const data = res.data;
                this.paperId = data.paperDetail.id;
                this.examTitle = data.paperDetail.paperName;

                if (data.paperDetail.limitTime > 0) {
                    this.timeLeft = data.paperDetail.limitTime * 60;
                    // this.startCountdown();
                }

                const formatQuestion = (q, type) => {
                    const base = {
                        id: q.id,
                        text: q.title,
                        type,
                        questionType: q.questionType,
                        score: q.score,
                        correct: q.correct,
                        userAnswer: type === "multiple" ? [] : "",
                        explanation: q.analysis,
                        showExplanation: false,
                        isCorrect: false,
                    };
                    if (type === "single" || type === "multiple") {
                        const options = [];
                        ["A", "B", "C", "D", "E"].forEach((letter) => {
                            const key = `answer${letter}`;
                            if (q[key]) {
                                options.push({ value: letter, label: q[key] });
                            }
                        });
                        base.options = options;
                    }
                    return base;
                };

                const single = (data.onlyQuestionList || []).map(q => formatQuestion(q, "single"));
                const multiple = (data.mulQuestionList || []).map(q => formatQuestion(q, "multiple"));
                const judge = (data.judgeQuestionList || []).map(q => ({
                    id: q.id,
                    text: q.title,
                    type: "truefalse",
                    questionType: q.questionType,
                    score: q.score,
                    correct: q.correct,
                    userAnswer: "",
                    explanation: q.analysis,
                    showExplanation: false,
                    isCorrect: false,
                    options: [
                        { value: "A", label: "正确" },
                        { value: "B", label: "错误" },
                    ],
                }));

                this.questionList = [...single, ...multiple, ...judge];
            } finally {
                if (this.loadingInstance) {
                    this.loadingInstance.close();
                }
            }
        },
        // 获取错题集
        async loadWrongPaper() {
            try {
                const res = await PaperApi.getChapterPaperWrongQuestion(
                    this.$route.params.courseId,
                    this.$route.params.chapterId,
                    this.$store.getters.userId
                );
                if (res.code !== 0 || !res.data) {
                    this.$confirm('暂无错题，快去答题吧~', '提示', {
                        confirmButtonText: '确定',
                        cancelButtonText: '取消',
                        type: 'warning'
                    }).then(() => {
                        this.$router.back();
                    }).catch(() => {
                        this.$router.back();
                    });
                    return; // ✅ 关键：不再往下执行，不会缓存
                }

                const data = res.data;

                // ✅ 判断是否有题目数据
                const hasNoQuestions = (!data.onlyQuestionList || data.onlyQuestionList.length === 0)
                    && (!data.mulQuestionList || data.mulQuestionList.length === 0)
                    && (!data.judgeQuestionList || data.judgeQuestionList.length === 0);

                if (hasNoQuestions) {
                    this.$confirm('暂无错题，快去答题吧~', '提示', {
                        confirmButtonText: '确定',
                        cancelButtonText: '取消',
                        type: 'warning'
                    }).then(() => {
                        this.$router.back();
                    }).catch(() => {
                        this.$router.back();
                    });
                    return; // ✅ 关键：不再往下执行，不会缓存
                }

                this.paperId = data.paperDetail.id;
                this.examTitle = data.paperDetail.paperName;

                if (data.paperDetail.limitTime > 0) {
                    this.timeLeft = data.paperDetail.limitTime * 60;
                    // this.startCountdown();
                }

                const formatQuestion = (q, type) => {
                    const base = {
                        id: q.id,
                        text: q.title,
                        type,
                        questionType: q.questionType,
                        score: q.score,
                        correct: q.correct,
                        userAnswer: type === "multiple" ? [] : "",
                        explanation: q.analysis,
                        showExplanation: false,
                        isCorrect: false,
                    };
                    if (type === "single" || type === "multiple") {
                        const options = [];
                        ["A", "B", "C", "D", "E"].forEach((letter) => {
                            const key = `answer${letter}`;
                            if (q[key]) {
                                options.push({ value: letter, label: q[key] });
                            }
                        });
                        base.options = options;
                    }
                    return base;
                };

                const single = (data.onlyQuestionList || []).map(q => formatQuestion(q, "single"));
                const multiple = (data.mulQuestionList || []).map(q => formatQuestion(q, "multiple"));
                const judge = (data.judgeQuestionList || []).map(q => ({
                    id: q.id,
                    text: q.title,
                    type: "truefalse",
                    questionType: q.questionType,
                    score: q.score,
                    correct: q.correct,
                    userAnswer: "",
                    explanation: q.analysis,
                    showExplanation: false,
                    isCorrect: false,
                    options: [
                        { value: "A", label: "正确" },
                        { value: "B", label: "错误" },
                    ],
                }));

                this.questionList = [...single, ...multiple, ...judge];
            } finally {
                if (this.loadingInstance) {
                    this.loadingInstance.close();
                }
            }
        },
        // 用户选择答案时触发
        onAnswerChange(question) {
            const val = question.userAnswer;

            // 用户当前的答案（字符串形式）
            const userAnswer = Array.isArray(val)
                ? [...val].sort().join("")
                : val;

            const correctAnswer = question.correct;

            // 👉 针对多选题：只有选项数量一致才显示解析和判断
            if (question.type === 'multiple') {
                if (val.length <= correctAnswer.length) {
                    // 答案不完整，先不显示解析
                    question.showExplanation = false;
                    question.isCorrect = false; // 可置为 null 或 false，取决于你样式需求
                    return;
                }
            }

            // ✅ 其余题型直接判断
            question.isCorrect = userAnswer === correctAnswer;
            question.showExplanation = true;

            this.saveProgress();
        },

        // 获取当前选项的样式 class
        getOptionClass(question, value) {
            if (question.userAnswer === "" || (Array.isArray(question.userAnswer) && question.userAnswer.length === 0)) {
                return "";
            }

            const isSelected = Array.isArray(question.userAnswer)
                ? question.userAnswer.includes(value)
                : question.userAnswer === value;

            const isCorrectAnswer = question.correct.includes(value);

            if (isSelected && isCorrectAnswer) {
                return "option-correct";
            } else if (isSelected && !isCorrectAnswer) {
                return "option-wrong";
            } else {
                return "";
            }
        },

        async submitAnswers() {
            const answerList = this.questionList.map((q) => {
                const userAnswer = Array.isArray(q.userAnswer)
                    ? q.userAnswer.sort().join("")
                    : q.userAnswer;
                
                const doRight = userAnswer === q.correct ? 0 : 1;

                return {
                    questionId: q.id,
                    questionType: q.questionType,
                    userAnswer,
                    doRight,
                    customerScore: doRight === 0 ? q.score : 0,
                };
            });

            const req = {
                classId: this.$route.params.classId,
                courseId: this.$route.params.courseId,
                chapterId: this.$route.params.chapterId,
                createStudent: this.$store.getters.userId,
                examPaperId: this.paperId,
                acquireScore: answerList.reduce((sum, item) => sum + item.customerScore, 0),
                answerReqList: answerList,
                type: this.$route.params.type,
            };

            const resp = await PaperApi.createPaperAnswer(req);
            this.$message.success("答卷已提交！");

            this.clearProgress();

            if (resp.code !== 0) return;

            this.$router.back();
        },
        startCountdown() {
            if (this.timeLeft <= 0) return; // 🛡️ 保护

            this.timer = setInterval(() => {
                if (this.timeLeft > 0) {
                    this.timeLeft--;
                } else {
                    clearInterval(this.timer);
                    this.submitAnswers();
                }
            }, 1000);
        },
        // 保存进度
        saveProgress() {
            const userId = this.$store.getters.userId;
            const cacheKey = `paper_cache_${this.$route.params.type}_${this.$route.params.courseId}_${this.$route.params.chapterId}_${userId}`;

            // 💡 附加试题内容本身
            const questionsSnapshot = this.questionList.map(q => ({
                id: q.id,
                text: q.text,
                type: q.type,
                questionType: q.questionType,
                score: q.score,
                correct: q.correct,
                userAnswer: q.userAnswer,
                explanation: q.explanation,
                showExplanation: q.showExplanation,
                isCorrect: q.isCorrect,
                options: q.options,
            }));

            const cacheData = {
                paperId: this.paperId,
                examTitle: this.examTitle,
                timeLeft: this.timeLeft,
                isCardCollapsed: this.isCardCollapsed,
                questionList: questionsSnapshot, // ⬅️ 加这个
                saveTime: Date.now(),
            };

            localStorage.setItem(cacheKey, JSON.stringify(cacheData));
        },
        // 提交成功或离开清空缓存
        clearProgress() {
            const userId = this.$store.getters.userId;
            const cacheKey = `paper_cache_${this.$route.params.type}_${this.$route.params.courseId}_${this.$route.params.chapterId}_${userId}`;
            localStorage.removeItem(cacheKey);
        },
    },
    beforeDestroy() {
        clearInterval(this.timer);
    },
};
</script>

<style scoped>
.quiz-container {
    background-color: #f4f4f4;
    min-height: 100vh;
    padding: 20px;
}

.quiz-header {
    background-color: #ffffff;
    padding: 15px;
    text-align: center;
    font-size: 18px;
    font-weight: bold;
    border-bottom: 1px solid #e0e0e0;
}

.quiz-content {
    display: flex;
    max-width: 1200px;
    margin: 20px auto;
    gap: 20px;
}

.question-area {
    flex: 2;
}

.question-card {
    background-color: #f9f9f9;
    margin-bottom: 15px;
    padding: 15px;
    border-radius: 5px;
}

.question-title {
    font-weight: bold;
    margin-bottom: 10px;
}

.question-options {
    margin-top: 10px;
}

.option-item {
    display: block;
    margin: 6px 0;
}

.question-analysis {
    margin-top: 10px;
}

.explanation {
    background-color: #ffffff;
    padding: 10px;
    border-radius: 5px;
    font-size: 14px;
}

.question-card-panel {
    min-width: 220px;
    flex: 0;
    background: #fff;
    padding: 10px;
    border-radius: 8px;
    border: 1px solid #eee;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
    position: sticky;
    top: 20px;
    height: fit-content;
}

.card-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 10px;
}

.card-title {
    font-weight: bold;
}

.question-nav {
    display: grid;
    grid-template-columns: repeat(5, 1fr);
    gap: 6px;
    max-height: 230px;
    overflow-y: auto;
    padding: 5px;
}

.question-nav button {
    width: 36px;
    height: 36px;
    font-size: 13px;
    text-align: center;
    justify-content: center;
    align-items: center;
    display: flex;
    padding: 0;
    border-radius: 4px;
    border: 1px solid #dcdfe6;
    background-color: #fff;
    transition: 0.2s;
    margin: 0;
}

.answered,
.answered:hover {
    background-color: #409eff !important;
    color: white !important;
}

.answered {
    background-color: #409eff !important;
    color: white;
}

.countdown {
    font-size: 14px;
    color: #f56c6c;
    font-weight: bold;
    text-align: center;
    margin-bottom: 10px;
    padding: 6px;
    border-radius: 4px;
    background-color: #fff7f7;
    border: 1px solid #fbc4c4;
}

.time-text {
    font-size: 16px;
}

.option-correct {
    background-color: #e6f9ec;
    border: 1px solid #67c23a;
    border-radius: 4px;
}

.option-wrong {
    background-color: #fdecea;
    border: 1px solid #f56c6c;
    border-radius: 4px;
}

.feedback-correct {
    color: #67c23a;
    font-weight: bold;
    margin-bottom: 6px;
}

.feedback-wrong {
    color: #f56c6c;
    font-weight: bold;
    margin-bottom: 6px;
}
</style>
